import type { Profile } from "@roof-hawk/core";
import type React from "react";
import { useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { fetchGmailAccounts } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import {
  fetchCompanySettings,
  type CompanySettingsRow,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";

/**
 * The `error` sentence from a non-2xx function response, if there is one.
 *
 * Null when there is no such body — the function is missing, or the network
 * failed — so the caller falls back to supabase-js's own message.
 */
async function refusal(fnError: unknown): Promise<string | null> {
  const context = (fnError as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  try {
    const body = (await context.clone().json()) as { error?: unknown };
    return typeof body.error === "string" && body.error.trim() !== ""
      ? body.error
      : null;
  } catch {
    return null;
  }
}

const messageOf = (problem: unknown): string =>
  problem instanceof Error ? problem.message : String(problem);

type SendResult = Record<string, unknown> & { sent?: string[]; error?: string };

/** Ask the function to send now, and hand back what it said. */
async function sendScheduled(functionName: string): Promise<SendResult> {
  const { data, error: problem } = await supabase.functions.invoke(
    functionName,
    { body: {} },
  );
  // A real failure comes back non-2xx, which supabase-js hands over as a
  // FunctionsHttpError with the Response on `context` and `data` null. The
  // function's own sentence is in that body, and it is the useful part —
  // "reconnect Google", "sent but not recorded" — so it is read first.
  if (problem) throw new Error((await refusal(problem)) ?? problem.message);
  return (data ?? {}) as SendResult;
}

type Person = Pick<Profile, "id" | "full_name" | "email" | "active">;

/** Who can send it, who can receive it, and who is sending it now. */
function people(
  data:
    | { profiles: Person[]; connected: Set<string>; from: string | null }
    | null
    | undefined,
) {
  const profiles = data?.profiles ?? [];
  const connected = data?.connected ?? new Set<string>();
  const from = data?.from ?? null;
  return {
    connected,
    from,
    senders: profiles.filter(
      (person) => person.active !== false && connected.has(person.id),
    ),
    // A recipient needs an address on their staff record and nothing else —
    // they are being written to, not writing.
    readers: profiles.filter(
      (person) => person.active !== false && (person.email ?? "").trim() !== "",
    ),
    sender: profiles.find((person) => person.id === from) ?? null,
  };
}

export interface ScheduledEmailProps {
  /** The row's name, e.g. "Thursday payout reminder". */
  title: string;
  /** What it is and when it goes, said under the title. */
  blurb: React.ReactNode;
  /** The Edge Function that sends it. */
  functionName: string;
  /** The Send now button. */
  sendLabel: string;
  /** Who it comes from and goes to, out of the settings row. */
  read: (settings: CompanySettingsRow | null) => {
    from: string | null;
    to: string[];
  };
  save: (input: {
    fromProfileId: string | null;
    profileIds: string[];
  }) => Promise<void>;
  /** The note after a send, from the function's answer. */
  sentNote: (result: SendResult) => string;
}

/**
 * A scheduled email from one person's mailbox to a list of staff.
 *
 * Two settings, and between them they are the switch: with nobody to send it
 * from, or nobody to send it to, it is not sent, and the function says which
 * of the two it was rather than reporting a success nobody received.
 *
 * Only somebody who has connected Google can be the sender, because the mail
 * leaves through their account — choosing a name that cannot send is the same
 * silent failure one layer up.
 *
 * The Thursday payout reminder (ADR 0177) and the monthly insurance email
 * (ADR 0275) are both this.
 */
export function ScheduledEmail({
  title,
  blurb,
  functionName,
  sendLabel,
  read,
  save: persist,
  sentNote,
}: ScheduledEmailProps) {
  const { isAdmin } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const query = useAsync(async () => {
    const [profiles, accounts, settings] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, full_name, email, active")
        .order("sort_order")
        .order("full_name"),
      fetchGmailAccounts(),
      fetchCompanySettings(),
    ]);
    return {
      profiles: unwrap<Person[]>(profiles),
      connected: new Set(accounts.map((a) => a.profile_id)),
      ...read(settings),
    };
  }, []);

  const saved = query.data?.to;
  useEffect(() => {
    if (saved) setChosen(new Set(saved));
  }, [saved]);

  if (query.loading) return <div className="stub">{t("Checking…")}</div>;

  const { connected, from, senders, readers, sender } = people(query.data);
  const off = !sender || chosen.size === 0;

  async function save(next: {
    fromProfileId?: string | null;
    ids?: Set<string>;
  }) {
    setSaving(true);
    setError(null);
    setNote(null);
    try {
      await persist({
        fromProfileId:
          next.fromProfileId === undefined ? from : next.fromProfileId,
        profileIds: [...(next.ids ?? chosen)],
      });
      await query.reload();
    } catch (problem) {
      setError(messageOf(problem));
    } finally {
      setSaving(false);
    }
  }

  function toggle(id: string) {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
    void save({ ids: next });
  }

  /**
   * Send it now.
   *
   * Here because the only other way to see the letter is to wait for the
   * schedule, and an email nobody has read once is an email nobody has
   * checked. It sends the real thing to the real list — there is no test
   * recipient, because a preview that goes somewhere else is not a preview.
   */
  async function sendNow() {
    setSending(true);
    setError(null);
    setNote(null);
    try {
      const result = await sendScheduled(functionName);
      if (result.error) setError(result.error);
      else setNote(sentNote(result));
    } catch (problem) {
      setError(messageOf(problem));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="rrow" style={{ marginTop: 24 }}>
        <span className="k">{title}</span>
        <span className="v">
          <span className={`badge ${off ? "draft" : "approved"}`}>
            {off ? t("off") : t("to {n}", { n: chosen.size })}
          </span>
        </span>
      </div>

      <ErrorNote error={error} />
      {note && (
        <div className="na-hint" style={{ maxWidth: 520 }}>
          {note}
        </div>
      )}

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 520 }}>
        {blurb}
      </p>

      {!isAdmin ? (
        <div className="na-hint" style={{ maxWidth: 520 }}>
          {t("Ask an admin to change this.")}
        </div>
      ) : (
        <Controls
          senders={senders}
          readers={readers}
          from={from}
          chosen={chosen}
          saving={saving}
          sending={sending}
          off={off}
          sendLabel={sendLabel}
          onFrom={(id) => void save({ fromProfileId: id })}
          onToggle={toggle}
          onSend={() => void sendNow()}
        />
      )}

      {sender && !connected.has(sender.id) && (
        <div className="shelf">
          <b>
            {t("{name} is no longer connected", {
              name: sender.full_name ?? "",
            })}
          </b>
          {t(
            "Nothing is being sent. Reconnect that account, or pick somebody else.",
          )}
        </div>
      )}
    </>
  );
}

/** Who it comes from, who it goes to, and Send now — admins only. */
function Controls({
  senders,
  readers,
  from,
  chosen,
  saving,
  sending,
  off,
  sendLabel,
  onFrom,
  onToggle,
  onSend,
}: {
  senders: Person[];
  readers: Person[];
  from: string | null;
  chosen: Set<string>;
  saving: boolean;
  sending: boolean;
  off: boolean;
  sendLabel: string;
  onFrom: (id: string | null) => void;
  onToggle: (id: string) => void;
  onSend: () => void;
}) {
  return (
    <>
      <label className="na-hint" style={{ display: "block", marginTop: 8 }}>
        {t("Sent from")}
      </label>
      {senders.length === 0 ? (
        <div className="shelf">
          <b>{t("Nobody has connected Gmail")}</b>
          {t("Connect an account under Tools → Integrations first.")}
        </div>
      ) : (
        <select
          className="addrow"
          aria-label={t("Sent from")}
          disabled={saving}
          value={from ?? ""}
          onChange={(event) => onFrom(event.target.value || null)}
        >
          <option value="">{t("Nobody — do not send it")}</option>
          {senders.map((person) => (
            <option key={person.id} value={person.id}>
              {person.full_name}
            </option>
          ))}
        </select>
      )}

      <label className="na-hint" style={{ display: "block", marginTop: 14 }}>
        {t("Sent to")}
      </label>
      {readers.map((person) => (
        <label key={person.id} style={{ display: "block", margin: "4px 0" }}>
          <input
            type="checkbox"
            disabled={saving}
            checked={chosen.has(person.id)}
            onChange={() => onToggle(person.id)}
          />{" "}
          {person.full_name}
        </label>
      ))}

      <button
        className="addrow"
        style={{ marginTop: 14 }}
        disabled={sending || off}
        onClick={onSend}
      >
        {sending ? t("Sending…") : sendLabel}
      </button>
    </>
  );
}
